import React, { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Checkbox,
  Button,
  Text,
  Flex,
  Loader,
  IconButton,
  Tooltip,
} from '@vibe/core'

import { Show, Download } from '@vibe/icons'
import {
  EvaluatePostulacionGate,
  PermissionGate,
} from '../common/PermissionGate'
import {
  exportCandidatesToExcel,
  exportSelectedCandidatesToExcel,
} from '../../utils/excelExport'
import QualificationStatusBadge from '../common/QualificationStatusBadge'
import { usePermissions } from '../../hooks/usePermissions'
import type { Candidate } from '../../types'
import { UserRole, QualificationStatus } from '../../types'

interface CandidatesTableProps {
  candidates: Candidate[]
  loading: boolean
  selectedCandidates: Set<string>
  onCandidateSelection: (candidateId: string, selected: boolean) => void
  onSelectAll: (selected: boolean) => void
  onBulkStatusUpdate: (status: 'califica' | 'no_califica' | 'pendiente') => void
  onBulkQualificationUpdate: (status: 'califica' | 'no_califica') => void
  onFilterChange: (
    filterType: 'status' | 'qualification',
    value: string
  ) => void | Promise<void>
  onSortChange: (field: string, order: 'asc' | 'desc') => void | Promise<void>
  onLoadMore?: () => void
  bulkUpdateLoading: boolean
  statusFilter: string
  qualificationFilter: string
  sortField: string
  sortOrder: 'asc' | 'desc'
  totalItems: number
  hasMore: boolean
  solicitudId?: string
  filterLoading?: boolean
}

/**
 * Simple status badge component
 */
const StatusBadge: React.FC<{
  status: string
  type: 'processing' | 'evaluation'
}> = ({ status, type }) => {
  const getConfig = () => {
    if (type === 'processing') {
      const configs = {
        uploading: { className: 'bg-blue-100 text-blue-800', text: 'Subiendo' },
        processing: {
          className: 'bg-blue-100 text-blue-800',
          text: 'Procesando',
        },
        completed: {
          className: 'bg-green-100 text-green-800',
          text: 'Completado',
        },
        error: { className: 'bg-red-100 text-red-800', text: 'Error' },
      }
      return (
        configs[status as keyof typeof configs] || {
          className: 'bg-gray-100 text-gray-800',
          text: status,
        }
      )
    } else {
      const configs = {
        pendiente: {
          className: 'bg-gray-100 text-gray-800',
          text: 'Pendiente',
        },
        califica: {
          className: 'bg-green-100 text-green-800',
          text: 'Califica',
        },
        no_califica: {
          className: 'bg-red-100 text-red-800',
          text: 'No Califica',
        },
      }
      return (
        configs[status as keyof typeof configs] || {
          className: 'bg-gray-100 text-gray-800',
          text: status,
        }
      )
    }
  }

  const config = getConfig()

  return (
    <span
      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.className}`}
    >
      {config.text}
    </span>
  )
}

/**
 * Match score indicator
 */
const MatchIndicator: React.FC<{ match?: number }> = ({ match }) => {
  if (match === undefined) {
    return <Text color="secondary">-</Text>
  }

  const getIndicatorColor = () => {
    if (match >= 70) return 'bg-green-500'
    if (match >= 50) return 'bg-blue-500'
    return 'bg-red-500'
  }

  return (
    <Flex align="center" gap="small">
      <Text weight="bold">{match}%</Text>
      <div className={`w-2 h-2 rounded-full ${getIndicatorColor()}`} />
    </Flex>
  )
}

/**
 * Candidates table component with filtering, sorting, and bulk actions
 */
export const CandidatesTable: React.FC<CandidatesTableProps> = ({
  candidates = [],
  loading,
  selectedCandidates,
  onCandidateSelection,
  onSelectAll,
  onBulkStatusUpdate,
  onBulkQualificationUpdate,
  onFilterChange,
  onSortChange,
  onLoadMore,
  bulkUpdateLoading,
  statusFilter,
  qualificationFilter,
  sortField,
  sortOrder,
  totalItems,
  hasMore,
  solicitudId,
  filterLoading = false,
}) => {
  const navigate = useNavigate()
  const { hasRole } = usePermissions()

  // Ensure candidates is always an array
  const safeCandidates = candidates || []

  // Check if user is a Viewer (and not also Editor/Admin)
  const isViewerOnly =
    hasRole(UserRole.VISUALIZADOR) &&
    !hasRole(UserRole.EDITOR) &&
    !hasRole(UserRole.ADMINISTRADOR)

  /**
   * Handle candidate row click
   */
  const handleCandidateClick = (candidateId: string) => {
    const url = solicitudId
      ? `/candidate/${candidateId}?solicitud=${solicitudId}`
      : `/candidate/${candidateId}`

    navigate(url, {
      state: { solicitudId },
    })
  }

  /**
   * Handle sort column click
   */
  const handleSortClick = (field: string) => {
    const newOrder = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc'
    onSortChange(field, newOrder)
  }

  /**
   * Get sort indicator
   */
  const getSortIndicator = (field: string) => {
    if (sortField !== field) return null
    return sortOrder === 'asc' ? '↑' : '↓'
  }

  /**
   * Check if all visible candidates are selected
   */
  const allSelected = useMemo(() => {
    return (
      safeCandidates.length > 0 &&
      safeCandidates.every(c => selectedCandidates.has(c.id_postulacion))
    )
  }, [safeCandidates, selectedCandidates])

  /**
   * Check if some candidates are selected (indeterminate state)
   */
  const someSelected = useMemo(() => {
    return selectedCandidates.size > 0 && !allSelected
  }, [selectedCandidates.size, allSelected])

  if (safeCandidates.length === 0 && !loading) {
    return (
      <div className="p-8 text-center">
        <Text>No se encontraron candidatos con los filtros aplicados.</Text>
      </div>
    )
  }

  return (
    <div className="p-6">
      {/* Header with filters and bulk actions */}
      <Flex justify="space-between" align="center" className="mb-6">
        <div>
          <Text type="text1" weight="bold" className="mb-2">
            Candidatos ({totalItems})
          </Text>
          {selectedCandidates.size > 0 && (
            <Text type="text3" color="secondary">
              {selectedCandidates.size} candidato(s) seleccionado(s)
            </Text>
          )}
        </div>

        <Flex gap="medium" align="center">
          {/* Download Actions */}
          <Flex gap="small" align="center">
            {/* Download All Candidates */}
            <Tooltip content="Descargar todos los candidatos como Excel">
              <Button
                leftIcon={Download}
                onClick={() =>
                  exportCandidatesToExcel(safeCandidates, 'candidatos')
                }
                disabled={safeCandidates.length === 0}
                kind="tertiary"
                size="small"
              >
                Descargar Excel
              </Button>
            </Tooltip>

            {/* Download Selected Candidates */}
            {selectedCandidates.size > 0 && (
              <Tooltip content="Descargar candidatos seleccionados como Excel">
                <Button
                  leftIcon={Download}
                  onClick={() =>
                    exportSelectedCandidatesToExcel(
                      safeCandidates,
                      selectedCandidates,
                      'candidatos_seleccionados'
                    )
                  }
                  kind="secondary"
                  size="small"
                >
                  Descargar Seleccionados ({selectedCandidates.size})
                </Button>
              </Tooltip>
            )}
          </Flex>

          {/* Qualification Filter - Hidden for Viewers */}
          {!isViewerOnly && (
            <div className="flex gap-2 items-center">
              <Text type="text3">Calificación:</Text>
              <Button
                kind={qualificationFilter === 'all' ? 'primary' : 'tertiary'}
                size="small"
                onClick={() => onFilterChange('qualification', 'all')}
              >
                Todas
              </Button>
              <Button
                kind={
                  qualificationFilter === 'pendiente' ? 'primary' : 'tertiary'
                }
                size="small"
                onClick={() => onFilterChange('qualification', 'pendiente')}
              >
                Pendiente
              </Button>
              <Button
                kind={
                  qualificationFilter === 'califica' ? 'primary' : 'tertiary'
                }
                size="small"
                onClick={() => onFilterChange('qualification', 'califica')}
              >
                Califica
              </Button>
              <Button
                kind={
                  qualificationFilter === 'no_califica' ? 'primary' : 'tertiary'
                }
                size="small"
                onClick={() => onFilterChange('qualification', 'no_califica')}
              >
                No Califica
              </Button>
            </div>
          )}

          {/* Message for Viewers */}
          {isViewerOnly && (
            <div className="flex items-center">
              <Text type="text3" color="secondary">
                Mostrando solo candidatos calificados
              </Text>
            </div>
          )}

          {/* Bulk Qualification Actions */}
          {selectedCandidates.size > 0 && (
            <PermissionGate
              requiredRoles={[UserRole.ADMINISTRADOR, UserRole.EDITOR]}
              requireAll={false}
            >
              <Flex gap="small">
                <Button
                  onClick={() => onBulkQualificationUpdate('califica')}
                  disabled={bulkUpdateLoading}
                  kind="primary"
                  size="small"
                >
                  Calificar Seleccionados
                </Button>
                <Button
                  onClick={() => onBulkQualificationUpdate('no_califica')}
                  disabled={bulkUpdateLoading}
                  kind="secondary"
                  size="small"
                >
                  No Calificar Seleccionados
                </Button>
              </Flex>
            </PermissionGate>
          )}
        </Flex>
      </Flex>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <PermissionGate
                  requiredRoles={[UserRole.ADMINISTRADOR, UserRole.EDITOR]}
                  requireAll={false}
                  fallback={<div className="w-4"></div>}
                >
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={e => onSelectAll(e.target.checked)}
                    ariaLabel="Seleccionar todos los candidatos"
                  />
                </PermissionGate>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSortClick('nombres')}
              >
                <Flex align="center" gap="small">
                  <Text>Nombre</Text>
                  {getSortIndicator('nombres')}
                </Flex>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-48 max-w-48">
                <Text>Cargo</Text>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSortClick('match')}
              >
                <Flex align="center" gap="small">
                  <Text>Match %</Text>
                  {getSortIndicator('match')}
                </Flex>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSortClick('qualification_status')}
              >
                <Flex align="center" gap="small">
                  <Text>Estado de Calificación</Text>
                  {getSortIndicator('qualification_status')}
                </Flex>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSortClick('fecha_registro')}
              >
                <Flex align="center" gap="small">
                  <Text>Fecha</Text>
                  {getSortIndicator('fecha_registro')}
                </Flex>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <Text>Acciones</Text>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {safeCandidates.map(candidate => (
              <tr
                key={candidate.id_postulacion}
                className="hover:bg-gray-50 cursor-pointer"
                onClick={() => handleCandidateClick(candidate.id_postulacion)}
              >
                <td
                  className="px-6 py-4 whitespace-nowrap"
                  onClick={e => e.stopPropagation()}
                >
                  <PermissionGate
                    requiredRoles={[UserRole.ADMINISTRADOR, UserRole.EDITOR]}
                    requireAll={false}
                    fallback={<div className="w-4"></div>}
                  >
                    <Checkbox
                      checked={selectedCandidates.has(candidate.id_postulacion)}
                      onChange={e =>
                        onCandidateSelection(
                          candidate.id_postulacion,
                          e.target.checked
                        )
                      }
                      ariaLabel={`Seleccionar candidato ${candidate.nombres} ${candidate.apellidos}`}
                    />
                  </PermissionGate>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <Text weight="bold">
                      {candidate.nombres} {candidate.apellidos}
                    </Text>
                    {candidate.correo && (
                      <Text type="text3" color="secondary" className="block">
                        {candidate.correo}
                      </Text>
                    )}
                    {candidate.telefono && (
                      <Text type="text3" color="secondary" className="block">
                        {candidate.telefono}
                      </Text>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 w-48 max-w-48">
                  <div className="break-words">
                    <div className="text-sm font-medium text-gray-900 break-words leading-tight whitespace-normal">
                      {candidate.cargo}
                    </div>
                    {candidate.empresa && (
                      <div className="text-xs text-gray-500 break-words leading-tight whitespace-normal mt-1">
                        {candidate.empresa}
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <MatchIndicator match={candidate.match} />
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <QualificationStatusBadge
                    status={candidate.qualification_status || 'pendiente'}
                    size="small"
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <Text type="text3">
                    {new Date(candidate.fecha_registro).toLocaleDateString(
                      'es-ES'
                    )}
                  </Text>
                </td>
                <td
                  className="px-6 py-4 whitespace-nowrap"
                  onClick={e => e.stopPropagation()}
                >
                  <Tooltip content="Ver detalles del candidato">
                    <IconButton
                      icon={Show}
                      size="small"
                      onClick={() =>
                        handleCandidateClick(candidate.id_postulacion)
                      }
                      ariaLabel="Ver detalles"
                    />
                  </Tooltip>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Load More Button */}
      {hasMore && (
        <Flex justify="center" className="mt-6">
          <Button onClick={onLoadMore} disabled={loading} kind="tertiary">
            {loading ? (
              <Flex align="center" gap="small">
                <Loader size="small" />
                <Text>Cargando...</Text>
              </Flex>
            ) : (
              'Cargar más candidatos'
            )}
          </Button>
        </Flex>
      )}

      {/* Bulk Update Loading Overlay */}
      {bulkUpdateLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 flex items-center gap-4">
            <Loader size="medium" />
            <Text>Actualizando estado de candidatos...</Text>
          </div>
        </div>
      )}
    </div>
  )
}
